import React from 'react';
// Map, Event et Comment sont des globales du navigateur : les importer sous
// leur nom nu les masquerait dans ce module (un `new Map()` casserait).
import AcUnit from '@mui/icons-material/AcUnit';
import AccessTime from '@mui/icons-material/AccessTime';
import AccountBalance from '@mui/icons-material/AccountBalance';
import AddPhotoAlternate from '@mui/icons-material/AddPhotoAlternate';
import Archive from '@mui/icons-material/Archive';
import BorderAllOutlined from '@mui/icons-material/BorderAllOutlined';
import CalendarToday from '@mui/icons-material/CalendarToday';
import Call from '@mui/icons-material/Call';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import CommentIcon from '@mui/icons-material/Comment';
import Construction from '@mui/icons-material/Construction';
import Create from '@mui/icons-material/Create';
import DateRange from '@mui/icons-material/DateRange';
import Description from '@mui/icons-material/Description';
import ElectricBolt from '@mui/icons-material/ElectricBolt';
import EuroSymbol from '@mui/icons-material/EuroSymbol';
import EventIcon from '@mui/icons-material/Event';
import FireTruck from '@mui/icons-material/FireTruck';
import Gavel from '@mui/icons-material/Gavel';
import HighlightOff from '@mui/icons-material/HighlightOff';
import HomeRepairService from '@mui/icons-material/HomeRepairService';
import HowToReg from '@mui/icons-material/HowToReg';
import Info from '@mui/icons-material/Info';
import Key from '@mui/icons-material/Key';
import LocalFireDepartment from '@mui/icons-material/LocalFireDepartment';
import Mail from '@mui/icons-material/Mail';
import MapIcon from '@mui/icons-material/Map';
import Payment from '@mui/icons-material/Payment';
import Person from '@mui/icons-material/Person';
import PhoneIphone from '@mui/icons-material/PhoneIphone';
import Plumbing from '@mui/icons-material/Plumbing';
import Receipt from '@mui/icons-material/Receipt';
import Send from '@mui/icons-material/Send';
import Sms from '@mui/icons-material/Sms';
import ThumbDown from '@mui/icons-material/ThumbDown';
import ThumbUp from '@mui/icons-material/ThumbUp';
import TripOrigin from '@mui/icons-material/TripOrigin';
import Warning from '@mui/icons-material/Warning';
import WhatsApp from '@mui/icons-material/WhatsApp';

/**
 * Résolution des icônes portées par @lba-dev/package.local-globals.
 *
 * Ce package est de la logique pure : il ne dépend pas de @mui et transporte
 * le *nom* du composant (`icon: 'Plumbing'`), pas le composant. Cette table
 * fait la résolution côté front, là où @mui est réellement installé.
 *
 * Les imports sont explicites plutôt qu'un `import * as Icons` : le barrel de
 * @mui/icons-material n'est pas tree-shaké et embarquerait plusieurs milliers
 * d'icônes dans le bundle.
 *
 * Pour ajouter une icône : ajoute son nom dans le fichier local-globals
 * concerné, puis son import ici.
 */

const icons = {
  AcUnit,
  AccessTime,
  AccountBalance,
  AddPhotoAlternate,
  Archive,
  BorderAllOutlined,
  CalendarToday,
  Call,
  CheckCircleOutline,
  Comment: CommentIcon,
  Construction,
  Create,
  DateRange,
  Description,
  ElectricBolt,
  EuroSymbol,
  Event: EventIcon,
  FireTruck,
  Gavel,
  HighlightOff,
  HomeRepairService,
  HowToReg,
  Info,
  Key,
  LocalFireDepartment,
  Mail,
  Map: MapIcon,
  Payment,
  Person,
  PhoneIphone,
  Plumbing,
  Receipt,
  Send,
  Sms,
  ThumbDown,
  ThumbUp,
  TripOrigin,
  Warning,
  WhatsApp
};

/**
 * Rend l'icône nommée, ou null si le nom est inconnu. Toutes les props sont
 * transmises au composant @mui (className, fontSize, style, sx...).
 */
/**
 * Garde-fou de développement.
 *
 * Un site de rendu oublié — `<elem.icon />` où `elem.icon` est désormais une
 * chaîne — produirait une balise inconnue et une icône simplement absente,
 * facile à ne pas voir.
 *
 * Ce garde ne lève pas : il journalise et accumule, pour qu'une seule session
 * de navigation révèle TOUS les sites manquants plutôt que de s'arrêter au
 * premier. Après avoir parcouru l'application :
 *
 *     window.__iconesNonMigrees()
 *
 * À retirer une fois la migration terminée.
 */
if (process.env.NODE_ENV === 'development') {
  const manquants = new Map();
  const createElement = React.createElement;

  React.createElement = function (type, ...rest) {
    if (typeof type === 'string' && icons[type]) {
      const pile = new Error().stack || '';
      if (!manquants.has(type)) {
        manquants.set(type, pile);
        // eslint-disable-next-line no-console
        console.error(
          `[icônes local-globals] "${type}" est rendue comme un composant. ` +
          'Ce champ porte un nom, pas un composant : remplace ' +
          `<x.icon /> par <LocalGlobalsIcon name={x.icon} />.\n${pile}`
        );
      }
    }
    return createElement.call(this, type, ...rest);
  };

  window.__iconesNonMigrees = () => {
    if (!manquants.size) {
      // eslint-disable-next-line no-console
      console.log('[icônes local-globals] aucun site manquant détecté.');
      return [];
    }
    // eslint-disable-next-line no-console
    console.table([...manquants.keys()].map((nom) => ({ icone: nom })));
    return [...manquants.entries()].map(([icone, pile]) => ({ icone, pile }));
  };
}

const LocalGlobalsIcon = ({ name, ...props }) => {
  const Icon = icons[name];
  return Icon ? <Icon {...props} /> : null;
};

export { icons };
export default LocalGlobalsIcon;
